<template lang="pug">
  #AdminSuperAssignPoints
    BNotification(:active.sync="showError" type="is-danger" :duration="3000")
      | {{ errorMessage }}
    BNotification(:active.sync="showSuccess" type="is-success" :duration="3000")
      | {{ successMessage }}

    .box.bordered
      WtTitle(h3) Add Points

      .mb-4
        BField(label="Points For")
          BSelect(
            v-model="selectedPointsFor"
            placeholder="Choose a type"
          )
            option(value="" disabled) -- Select Type --
            option(
              v-for="type in pointsForOptions"
              :key="type.value"
              :value="type.value"
            ) {{ type.label }}

      .mb-4
        BField(
          label="Points amount"
          :message="amountHelpText"
        )
          BInput(
            v-model.number="amount"
            type="number"
            min="1"
            placeholder="Leave blank for default"
          )

      .mb-4
        BField(label="Points Date")
          BDatepicker(
            v-model="pointsForDate"
            placeholder="Click to select..."
            :max-date="today"
            :min-date="member ? new Date(member.registeredDate) : null"
          )

      WtButton(
        @click.native="addPoints"
        :loading="addingPoints"
        :disabled="!canSubmit"
      ) Add Points
</template>

<script>
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import moment from "moment";

export default {
  name: "AdminSuperAssignPoints",

  components: {
    WtButton,
    WtTitle
  },

  props: {
    memberId: {
      type: Number,
      required: true
    },
    member: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      selectedPointsFor: "",
      amount: null,
      pointsForDate: null,
      addingPoints: false,
      showError: false,
      showSuccess: false,
      errorMessage: "",
      successMessage: "",
      today: new Date(),
      pointsForOptions: [
        { value: "entries", label: "Steps (entries)", defaultAmount: 15 },
        { value: "photos", label: "Photos", defaultAmount: 3 },
        { value: "blog", label: "Blog", defaultAmount: 1 },
        { value: "question", label: "Question", defaultAmount: 20 },
        { value: "nutrition", label: "Nutrition", defaultAmount: 5 },
        { value: "water", label: "Water", defaultAmount: 5 },
        { value: "sleep", label: "Sleep", defaultAmount: 5 },
        { value: "heart", label: "Heart", defaultAmount: 5 },
        { value: "weight", label: "Weight", defaultAmount: 15 },
        { value: "mindfulness", label: "Mindfulness", defaultAmount: 5 },
        { value: "mood", label: "Mood", defaultAmount: 5 }
      ]
    };
  },

  computed: {
    defaultAmount() {
      const option = this.pointsForOptions.find(
        o => o.value === this.selectedPointsFor
      );
      return option ? option.defaultAmount : 0;
    },

    amountHelpText() {
      if (!this.selectedPointsFor) {
        return "Select a type above to see the default amount";
      }
      return `Note: Most likely you should leave this blank and let the system figure it out. Unless it's for a question or you want to give a custom amount. Default: ${this.defaultAmount} points`;
    },

    canSubmit() {
      return this.selectedPointsFor && this.pointsForDate;
    }
  },

  methods: {
    async addPoints() {
      if (!this.canSubmit) {
        this.showErrorMessage("Please fill in all required fields");
        return;
      }

      if (this.member) {
        const registeredDate = this.member.registeredDate;
        if (moment(this.pointsForDate).isBefore(registeredDate, "day")) {
          const registeredDateFormatted = moment(registeredDate).format(
            "YYYY-MM-DD"
          );
          this.showErrorMessage(
            `Points date cannot be before member's registration date (${registeredDateFormatted})`
          );
          return;
        }
      }

      try {
        this.addingPoints = true;
        const payload = {
          amount: this.amount || null,
          pointsFor: this.selectedPointsFor,
          pointsForDate: moment(this.pointsForDate).format("YYYY-MM-DD")
        };

        await this.axios.post(`members/${this.memberId}/add-points`, payload);
        this.showSuccessMessage("Points added successfully");
        this.selectedPointsFor = "";
        this.amount = null;
        this.pointsForDate = null;
        this.$emit("points-added");
      } catch (error) {
        console.error("Error adding points:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to add points";
        this.showErrorMessage(errorMessage);
      } finally {
        this.addingPoints = false;
      }
    },

    showErrorMessage(message) {
      this.errorMessage = message;
      this.showError = true;
    },

    showSuccessMessage(message) {
      this.successMessage = message;
      this.showSuccess = true;
    }
  }
};
</script>

<style lang="sass" scoped>
#AdminSuperAssignPoints
  .box
    margin-bottom: 2rem
</style>
